// src/components/FormComponents/PlanCardsSelect.tsx
import React, { useCallback } from "react";
import { Box, Button, Card, Typography } from "@mui/material";

export type PlanItem = {
  value: string;
  title: string;
  discountText: string;
  descriptionLines: string[];
  ctaText: string;
  ctaDocPdf: string;
};

export function PlanCardsSelect({
  items,
  value,
  onChange,
  error,
  onCtaClick,
}: {
  items: PlanItem[];
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  onCtaClick?: (args: { planValue: string; ctaDocPdf: string }) => void;
}) {
  const isManyCards = items.length > 3;

  const handleSelect = useCallback(
    (nextValue: string) => {
      if (nextValue !== value) onChange(nextValue);
    },
    [onChange, value],
  );

  return (
    <Box sx={{ direction: "rtl" }} role="radiogroup" aria-invalid={!!error}>
      <Box
        sx={{
          display: "grid",
          gap: isManyCards ? 1.25 : 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: `repeat(${items.length}, minmax(0, 1fr))`,
          },
          alignItems: "stretch",
        }}
      >
        {items.map((item) => {
          const selected = item.value === value;

          return (
            <Box key={item.value}>
              <Card
                variant="outlined"
                role="radio"
                aria-checked={selected}
                tabIndex={0}
                onClick={() => handleSelect(item.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelect(item.value);
                  }
                }}
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  userSelect: "none",
                  bgcolor: "#0B0B0B",
                  color: "common.white",
                  borderColor: error
                    ? "error.main"
                    : selected
                      ? "success.main"
                      : "rgba(255,255,255,0.18)",
                  boxShadow: selected
                    ? "0 0 0 6px rgba(46, 204, 113, 0.95)"
                    : "none",
                  transition:
                    "transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease",
                  "&:hover": { transform: "translateY(-2px)" },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: selected ? "success.main" : "common.white",
                    outlineOffset: 2,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    height: "100%",
                    p: isManyCards ? 1.25 : 2,
                    gap: isManyCards ? 0.75 : 1.25,
                    direction: "rtl",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.75,
                      direction: "rtl",
                    }}
                  >
                    <Typography
                      dir="rtl"
                      variant="h6"
                      sx={{
                        fontWeight: 800,
                        direction: "rtl",
                        fontSize: isManyCards ? 16 : undefined,
                      }}
                    >
                      {item.title}
                    </Typography>

                    <Box
                      dir="rtl"
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.75,
                        direction: "rtl",
                      }}
                    >
                      <Typography
                        sx={{
                          marginBottom: "-10px",
                          fontSize: isManyCards ? "34pt" : "49pt"
                      }}
                      >
                        {item.discountText}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: isManyCards ? "30pt" : "43pt",
                          marginTop: isManyCards ? "-22px" : "-36px",
                          marginBottom: isManyCards ? "-8px" : "-15px",
                          lineHeight: 1,
                        }}
                      >
                        הנחה
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    dir="rtl"
                    sx={{
                      flex: 1,
                      pt: 0.5,
                      width: "100%",
                      direction: "rtl",
                    }}
                  >
                    {item.descriptionLines.map((line, idx) => (
                      <Typography
                        dir="rtl"
                        key={`${item.value}-${idx}`}
                        variant="body2"
                        sx={{
                          lineHeight: isManyCards ? 1.4 : 1.65,
                          fontSize: isManyCards ? 15 : 19,
                        }}
                      >
                        {line}
                      </Typography>
                    ))}
                  </Box>

                  <Button
                    component="a"
                    href={item.ctaDocPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    variant="contained"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCtaClick?.({
                        planValue: item.value,
                        ctaDocPdf: item.ctaDocPdf,
                      });
                    }}
                    sx={{
                      mt: 1,
                      bgcolor: "#01F156",
                      color: "common.black",
                      fontWeight: 800,
                      borderRadius: 1,
                      fontSize: isManyCards ? 14 : 18,
                      py: isManyCards ? 0.75 : undefined,
                      "&:hover": { bgcolor: "success.dark" },
                    }}
                  >
                    {item.ctaText}
                  </Button>
                </Box>
              </Card>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
